import {seatingsPricingOptionModalOpened, seatingsPricingOptionModalSelected} from '@wix/bi-logger-events-uou/v2'
import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {BI_ORIGINS, getPricingOptionLabel, getPricingOptions} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment, useTranslation, useVisitorBi} from '@wix/yoshi-flow-editor'
import React, {useCallback, useEffect, useState} from 'react'
import {InputDialog, RadioButton, RadioButtonGroup, RadioButtonTheme} from 'wix-ui-tpa/cssVars'
import {useCurrencyFormatter} from '../../../../../../../../commons/hooks/currency'
import {useColors} from '../../../../../hooks/colors'
import {isPlaceArea, pricingOptionPlaceId} from '../../../../../selectors/seating/place'
import {getFees} from '../../../../../selectors/tax-and-fee'
import {TicketInfoTexts} from '../../ticket/ticket-info/ticket-info-texts'
import {Footer} from '../footer'
import {PricingOptionModalProps} from './interfaces'
import {classes, st} from './pricing-option-modal.st.css'

export const PricingOptionModal = ({
  place,
  event,
  setShowPricingOptionsPlace,
  selectPlacePricingOption,
  noServiceFee,
}: PricingOptionModalProps) => {
  const {t} = useTranslation()
  const {isMobile} = useEnvironment()
  const bi = useVisitorBi()
  const {darkColor} = useColors()
  const pricingOptions = getPricingOptions(place.ticket)
  const area = isPlaceArea(place)
  const [selectedOptionId, setSelectedOptionId] = useState<string>(pricingOptions[0].id)
  const {getFormattedMoney} = useCurrencyFormatter()

  const biFields = {
    pricing_options: JSON.stringify(pricingOptions),
    ticket_definition_id: place.ticket.id,
    is_whole_table: false,
    seat_guid: place.planPlaceId,
    element_type: place.elementType,
    element_guid: place.planPlaceId,
    event_id: event.id,
  }

  useEffect(() => {
    focusElement({selector: `${hookToAttributeSelector(DH.PRICING_OPTION)} input`, canFocus: true})
    bi.report(seatingsPricingOptionModalOpened(biFields))
  }, [])

  const close = useCallback(() => {
    setShowPricingOptionsPlace(null)
    bi.report(
      seatingsPricingOptionModalSelected({
        selection: 'Close',
        ...biFields,
      }),
    )
  }, [setShowPricingOptionsPlace])

  const confirm = useCallback(() => {
    const placeId = area ? pricingOptionPlaceId(place.planPlaceId, selectedOptionId) : place.id
    selectPlacePricingOption({placeId, pricingOptionIds: [selectedOptionId], origin: BI_ORIGINS.MAP})
    bi.report(
      seatingsPricingOptionModalSelected({
        selection: 'Add to basket',
        selected_pricing_options: JSON.stringify([pricingOptions.find(({id}) => id === selectedOptionId)]),
        ...biFields,
      }),
    )
    setShowPricingOptionsPlace(null)
  }, [place.id, selectedOptionId, selectPlacePricingOption, setShowPricingOptionsPlace])

  return (
    <InputDialog
      title={t('seating_priceOptionsSingle_title')}
      isOpen={true}
      onClose={close}
      className={isMobile ? classes.mobileModalRoot : undefined}
      fullscreen={isMobile}
      data-hook={DH.PRICING_OPTIONS_MODAL}
      closeButtonAriaLabel={t('a11y.close')}
      ariaLabel={t('seating_priceOptionsSingle_title')}
      customFooter={
        <Footer
          primaryButtonLabel={t('seating_priceOptionsSingle_confirmButton')}
          primaryButtonClick={confirm}
          secondaryButtonLabel={t('seating_priceOptionsSingle_cancelButton')}
          secondaryButtonClick={close}
        />
      }
    >
      <div className={st(classes.optionsContainer, {mobile: isMobile})}>
        {isMobile ? (
          <div className={classes.ticketInfo}>
            <TicketInfoTexts place={place} inPricingOptionsModal />
          </div>
        ) : null}
        <RadioButtonGroup
          className={classes.options}
          data-hook={DH.PRICING_OPTIONS}
          name="pricing-option"
          onChange={value => setSelectedOptionId(value)}
          theme={RadioButtonTheme.Box}
          value={selectedOptionId}
        >
          {pricingOptions.map(({id, price}) => (
            <RadioButton
              data-hook={DH.PRICING_OPTION}
              key={id}
              className={st(classes.option, {blackAndWhite: !darkColor || id !== selectedOptionId})}
              value={id}
              label={getPricingOptionLabel(place.ticket, id)}
              subtext={getFees({
                event,
                ticket: place.ticket,
                t,
                getFormattedMoney,
                overridePrice: price.value,
                noServiceFee,
              })}
            />
          ))}
        </RadioButtonGroup>
      </div>
    </InputDialog>
  )
}
